<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Purchasing" backTo="internal">
      <router-link
          :to="{ name: 'purchase-create' }"
          class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M12 5v14M5 12h14"/>
        </svg>
        <span>Create Purchase</span>
      </router-link>
    </page-header>

    <div class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <!-- Toolbar with Tab Headers and Search Controls -->
        <div class="w-full flex items-center justify-between">
          <div class="flex -mx-0 mr-2">
            <TabHeaders :tabs="tabs" :selectedTab="currentTab" @tab-selected="onTabSelected"/>
          </div>
          <div class="flex flex-col flex-1">
            <div class="flex justify-end">
              <div class="flex items-center mr-4">
                <label class="mr-2">From Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="fromDateInput"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>
              <div class="flex items-center ">
                <label class="mr-2">To Date:</label>
                <Calendar
                    class="w-full lg:w-auto"
                    v-model="toDateInput"
                    dateFormat="dd/mm/yy"
                    :showIcon="true"
                />
              </div>


              <input
                  type="text"
                  class="ml-6 w-full lg:w-auto rounded-l bge-input bge-input-spacing"
                  placeholder="Search"
                  v-model="searchTextInput"
                  @keydown.enter="triggerSearch"
              />
              <button
                  @click="triggerSearch"
                  class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
              >
                <Icon iconType="search"/>
              </button>
              <SearchTipsInfo :tips="searchTips" class="ml-1"/>
            </div>
          </div>
        </div>

        <!-- Tab Content -->
        <TabContent :currentTab="currentTab">
          <template v-slot:[currentTab]>
            <DataTableComponent :columns="purchase_columns" @row-click="onRowClick" rowGroupMode="rowspan"
                                key="all_purchases" :rowClass="rowClass"
            >

              <template v-slot:status-slot="{ rowData }">
                {{ checkStatusAndReturnUppercase(rowData.status) }}
              </template>

              <template v-slot:order-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.order_date) }}
              </template>

              <template v-slot:delivery-date-slot="{ rowData }">
                <!--                  <span :class="getDeliveryDateClass(rowData)">-->
                {{ formatTimestamp(rowData.requested_delivery_date) }}
                <!--                  </span>-->
              </template>

              <template v-slot:received-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.received_date) }}
              </template>

              <template v-slot:approved-date-slot="{ rowData }">
                {{ formatTimestamp(rowData.approved_date) }}
              </template>

              <template v-slot:action-slot="{ rowData }">
                <div class="flex items-center space-x-2">

                  <div v-if="showCancelOrderButton(rowData.status)" title="CANCEL ORDER">
                    <AppButton
                        color="red"
                        label=""
                        :icon="true"
                        @click.native.stop="cancelOrder(rowData.purchase_order_id)"
                    >
                      <template v-slot:icon>
                        <Icon iconType="circle-minus"/>
                      </template>
                    </AppButton>
                  </div>

                  <div title="RECEIVED PROGRESS">
                    <Knob
                        :value="getReceivedCounts(rowData)"
                        :min="0"
                        :max="getTotalItems(rowData)"
                        :size="35"
                        valueColor="#4CAF50"
                        :strokeWidth="10"
                    />
                  </div>

                  <div v-if="showCopyOrderButton(rowData.status)" title="COPY ORDER">
                    <AppButton
                        color="blue"
                        label=""
                        :icon="true"
                        @click.native.stop="copyOrder(rowData.purchase_order_id)"
                    >
                      <template v-slot:icon>
                        <Icon iconType="copy-plus"/>
                      </template>
                    </AppButton>
                  </div>

                  <div title="DELETE ORDER">
                    <AppButton
                        color="red"
                        label=""
                        :icon="true"
                        @click.native.stop="deleteOrder(rowData.purchase_order_id)"
                    >
                      <template v-slot:icon>
                        <Icon iconType="delete"/>
                      </template>
                    </AppButton>
                  </div>

                </div>
              </template>


            </DataTableComponent>
          </template>
        </TabContent>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Spinner from "@/components/Spinner.vue";
import Panel from "@/components/Panel.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import TabContent from "@/components/TabContent.vue";
import DataTableComponent from "@/views/HR/Purchases/Components/DataTableComponent.vue";
import {mapActions, mapState} from "vuex";
import Icon from "@/components/Icon.vue";
import notificationMixin from "@/mixins/notificationMixin";
import StaffAutocomplete from "@/components/AutoComplete/StaffAutoComplete.vue";
import AppButton from "@/components/Button.vue";
import SearchTipsInfo from "@/components/AutoComplete/SearchTipsInfo.vue";

export default {
  mixins: [notificationMixin],
  components: {
    AppButton,
    PageHeader,
    Spinner,
    Panel,
    TabHeaders,
    TabContent,
    DataTableComponent,
    Icon,
    StaffAutocomplete,
    SearchTipsInfo
  },
  data() {
    return {
      tabs: [
        {id: "all_purchases", label: "All Purchases",},
        {id: "pending", label: "Pending",},
        {id: "all_received", label: "All Received",},
        {id: "approved", label: "Approved",},
      ],
      searchTips: [
        {example: '#urgent', text: 'internal notes only'},
        {example: 'samoa + 12323', text: 'all terms must match'},
        {example: '12323', text: 'searches PO no., supplier, postcode, invoice no., product code, delivery & internal notes'},
      ],
      purchase_columns: [
        {
          field: "purchase_order_no",
          header: "Purchase No.",
          sortable: true,
          custom: false,
          width: '15%',
        },
        {
          field: "order_date",
          header: "Order Date",
          sortable: true,
          custom: true,
          slotName: "order-date-slot",
          width: '10%',
        },
        {
          field: "supplier.name",
          header: "Supplier",
          sortable: true,
          custom: false,
          width: '20%',
        },
        {
          field: "requested_delivery_date",
          header: "Requested Del Date",
          sortable: true,
          custom: true,
          slotName: "delivery-date-slot",
          width: '15%',
        },
        {
          field: "received_date",
          header: "Received Date",
          sortable: true,
          custom: true,
          slotName: "received-date-slot",
          width: '10%',
        },
        {
          field: "status",
          header: "Status",
          sortable: true,
          custom: true,
          slotName: "status-slot",
          width: '10%',
        },
        {
          field: "approved_date",
          header: "Approved Date",
          sortable: true,
          custom: true,
          slotName: "approved-date-slot",
          width: '10%',
        },
        {
          field: "action",
          header: "",
          sortable: false,
          custom: true,
          slotName: "action-slot",
          width: '10%',
        },
      ],
    };
  },
  computed: {
    ...mapState("purchase", ["currentTab", "searchText", "fromDate", "toDate","lastSelectedPurchase"]),

    searchTextInput: {
      get() {
        return this.searchText;
      },
      set(value) {
        this.$store.commit("purchase/UPDATE_SEARCH_TEXT", value);
      },
    },

    selectedEngineerInput: {
      get() {
        return this.selectedEngineer;
      },
      set(value) {
        this.$store.commit("purchase/UPDATE_SELECTED_ENGINEER", value);
      },
    },

    fromDateInput: {
      get() {
        const fromDate = this.fromDate;
        return fromDate ? new Date(fromDate) : null;
      },
      set(value) {
        this.$store.commit("purchase/UPDATE_FROM_DATE", value);
      },
    },

    toDateInput: {
      get() {
        const toDate = this.toDate;
        return toDate ? new Date(toDate) : null;
      },
      set(value) {
        this.$store.commit("purchase/UPDATE_TO_DATE", value);
      },
    },
  },
  mounted() {
    this.fetchTableData();
  },
  methods: {
    ...mapActions("purchase", [
      "fetchTableData",
      "updateSearchCriteria",
      "updateTab",
      "resetCurrentPage",
    ]),

    validateDates() {
      const hasFromDate = !!this.fromDateInput;
      const hasToDate = !!this.toDateInput;

      if (hasFromDate && hasToDate) {
        if (this.toDateInput < this.fromDateInput) {
          this.notifyWarning("To Date should not be earlier than From Date.");
          return false;
        }
        return true;
      }

      if (hasFromDate !== hasToDate) {
        this.notifyWarning("Please select both the From Date and To Date.");
        return false;
      }

      return true;
    },

    onTabSelected(newTab) {

      this.updateTab(newTab);
    },

    triggerSearch() {
      if (this.validateDates()) {
        this.resetCurrentPage(0);
        this.updateTab("all_purchases");
      }
    },

    onRowClick(event) {

      this.$store.commit("purchase/SET_LAST_SELECTED_PURCHASE", event.data.purchase_order_id);

      this.$router.push({
        name: 'purchase-edit',
        params: {purchase_order_id: event.data.purchase_order_id}
      });
    },
    formatTimestamp(unixTimestamp) {

      if (!unixTimestamp) {
        return ''; // or any default value like 'N/A'
      }
      return this.$moment.unix(unixTimestamp).format('DD-MM-YYYY');
    },
    checkStatusAndReturnUppercase(status) {
      if (status && status.toLowerCase() === 'items added') {
        return 'PENDING';
      }
      return status ? status.toUpperCase() : 'N/A';
    },
    getDeliveryDateClass(rowData) {
      if (['part received', 'items added'].includes(rowData.status)) {
        return 'expiry-badge status-' + this.getDeliveryDateStatus(rowData.requested_delivery_date);
      }
      return 'expiry-badge status-normal'
    },
    getDeliveryDateStatus(requestedDeliveryDate) {
      const today = this.$moment();
      const deliveryDate = this.$moment.unix(requestedDeliveryDate);
      const daysDiff = deliveryDate.diff(today, "days");

      if (daysDiff < 0) return "row-red-light"; // Past due date
      if (daysDiff <= 14) return "row-orange-light"; // Due within 14 days
      return "normal"; // No specific color
    },
    rowClass(rowData) {
      const allowedStatuses = ["items added", "pending", "part received"];
      let classes = [];

      if (allowedStatuses.includes(rowData.status)) {
        const rowColor = this.getDeliveryDateStatus(rowData.requested_delivery_date);
        if (rowColor === "row-red-light") {
          classes.push("row-red-light");
        } else if (rowColor === "row-orange-light") {
          classes.push("row-orange-light");
        }
      }

      if (rowData.purchase_order_id === this.lastSelectedPurchase) {
        classes.push("highlight-row");
      }

      return classes.join(" ");
    },
    calculateReceivedCounts(data) {
      if (!data || !data.items || !data.items.length) {
        return {totalItems: 0, totalReceived: 0};
      }

      let totalItems = 0;
      let totalReceived = 0;

      data.items.forEach(item => {
        totalItems += parseFloat(item.quantity || 0);
        totalReceived += item.received_items ? item.received_items.reduce((receivedTotal, receivedItem) => {
          return receivedTotal + parseFloat(receivedItem.quantity_received || 0);
        }, 0) : 0;
      });

      return {totalItems, totalReceived};
    },
    getReceivedCounts(rowData) {
      const counts = this.calculateReceivedCounts(rowData);
      return counts.totalReceived;
    },
    getTotalItems(rowData) {
      const counts = this.calculateReceivedCounts(rowData);
      return counts.totalItems;
    },
    showCancelOrderButton(status) {
      const statuses = ['part received', 'items added', 'pending'];
      return statuses.includes(status);
    },
    showCopyOrderButton(status) {
      const statuses = ['part received','received', 'approved', 'cancelled', 'items added', 'pending'];
      return statuses.includes(status);
    },

    async cancelOrder(purchaseOrderId) {
      const confirmation = await this.confirmAction({
        message: 'Do you want to cancel this order?',
        header: 'Confirm Action',
        acceptLabel: 'Yes',
        rejectLabel: 'No'
      });

      if (confirmation) {
        try {
          const cancelledDate = this.$moment().unix();

          const updateData = {
            status: 'cancelled',
            cancelled_date: cancelledDate,
          };

          if (purchaseOrderId) {
            await this.PurchaseOrderService.updatePurchaseOrder(
                purchaseOrderId,
                updateData,
            );

            await this.fetchTableData()
            this.notifySuccess("Purchase order cancelled successfully");

          } else {
            this.notifyError("Purchase order ID is missing");
          }

        } catch (error) {
          this.notifyError("Couldn't cancel the purchase order");
          console.error(error);
        } finally {

        }
      }
    },

    async copyOrder(purchaseOrderId) {
      const confirmation = await this.confirmAction({
        message: 'Do you want to copy this order?',
        header: 'Confirm Action',
        acceptLabel: 'Yes',
        rejectLabel: 'No'
      });

      if (confirmation) {
        try {
          if (purchaseOrderId) {
            const newOrder = await this.PurchaseOrderService.copyPurchaseOrder(purchaseOrderId);
            this.notifySuccess("Purchase order copied successfully");
            // Redirect to the new order page or handle as needed

            await this.fetchTableData()
            this.$store.commit("purchase/SET_LAST_SELECTED_PURCHASE", newOrder.purchase_order_id);

            // await this.$router.push({
            //   name: 'purchase-edit',
            //   params: {purchase_order_id: newOrder.purchase_order_id}
            // });

          } else {
            this.notifyError("Purchase order ID is missing");
          }

        } catch (error) {
          this.notifyError("Couldn't copy the purchase order");
          console.error(error);
        } finally {
        }
      }
    },
    async deleteOrder(purchaseOrderId) {
      const confirmation = await this.confirmAction({
        message: 'Do you want to delete this order?',
        header: 'Confirm Action',
        acceptLabel: 'Yes',
        rejectLabel: 'No'
      });

      if (confirmation) {
        try {
          if (purchaseOrderId) {
            await this.PurchaseOrderService.deletePurchaseOrder(purchaseOrderId);
            this.notifySuccess("Purchase order deleted successfully");
            await this.fetchTableData()
            // this.$router.go(-1);
          } else {
            this.notifyError("Purchase order ID is missing");
          }

        } catch (error) {
          this.notifyError("Couldn't delete the purchase order");
          console.error(error);
        } finally {
        }
      }
    },
  },
};
</script>

<style scoped>

::v-deep .row-red-light {
  color: red !important; /* Light shade of red */
}

::v-deep .row-orange-light {
  color: orange !important; /* Light shade of orange */
}

::v-deep .highlight-row {
  background-color: rgba(0,0,0,.15) !important;
}

</style>
